<script lang="ts">
    // @ts-nocheck
    import BaseTab from "@/tabs/mixins/BaseTab.js";
    import tabIcon from "@/assets/vardplatsbestallning.svg";

    export default {
        // eslint-disable-next-line vue/multi-word-component-names
        name: "Vardplatsbestallning",
        tabName: "Vårdplatsbeställning",
        extends: BaseTab,
        patientBound: true,
        icon: tabIcon,

        methods: {
            cancel({ hasUnsavedData }: { hasUnsavedData: boolean }) {
                this.closeTab(hasUnsavedData);
            },
        },
    };
</script>

<script setup lang="ts">
    import { ref, toRefs, computed, onBeforeMount, onBeforeUnmount } from "vue";
    import { getErrorMessage } from "@/api";
    import { flashMessage, flashWarning, openDialog } from "@/utils";
    import { useStore } from "@/store";
    import { VardplatsStatus } from "@/tabs/Vardplatsbokning/types";
    import VardplatsbestallningForm from "@/tabs/Vardplatsbokning/VardplatsbestallningForm.vue";
    import VardplatsbestallningDisplay from "@/tabs/Vardplatsbokning/VardplatsbestallningDisplay.vue";
    import moment from "moment";
    import StatusBestalldIcon from "@/assets/status_bestalld.svg";
    import StatusBokadIcon from "@/assets/statusbokad.svg";
    import StatusInskrivenIcon from "@/assets/status_inskriven.svg";
    import StatusUtskrivenIcon from "@/assets/status_utskriven.svg";
    import StatusStrukenIcon from "@/assets/status_struken.svg";
    import StatusInProgressIcon from "@/assets/status_in_progress.svg";

    const tabName = "Vårdplatsbeställning";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
        parameters?: {
            id?: string;
        };
    }>();

    const { tabId, parameters } = toRefs(props);

    const vardplatsbestallning = computed(() =>
        store.getters["vardplatsbestallning/vardplatsbestallningById"]({
            id: parameters?.value?.id,
        })
    );

    const readOnly = computed(() => {
        return vardplatsbestallning?.value?.externt_id || vardplatsbestallning?.value?.status === "Utskriven";
    });

    const createdDate = computed(() => {
        if (vardplatsbestallning?.value) {
            return moment(vardplatsbestallning?.value?.created_at ?? null).format("YYYY-MM-DD");
        }
        return moment().format("YYYY-MM-DD");
    });

    const createdBy = computed(() => {
        if (vardplatsbestallning?.value) {
            const profile = vardplatsbestallning?.value?.created_by_display;
            return profile ? `av ${profile.user_display?.name}, ${profile.roll_display?.displayName}` : "";
        }
        return ` av ${store.state.userData.userData.name}, ${store.state.userData.currentProfile.roll.displayName}`;
    });

    const statusIcon = computed(() => {
        const status = vardplatsbestallning?.value?.status;

        switch (status) {
            case VardplatsStatus.BESTALLD:
                return StatusBestalldIcon;
            case VardplatsStatus.BOKAD:
                return StatusBokadIcon;
            case VardplatsStatus.INSKRIVEN:
                return StatusInskrivenIcon;
            case VardplatsStatus.UTSKRIVEN:
                return StatusUtskrivenIcon;
            case VardplatsStatus.STRUKEN:
                return StatusStrukenIcon;
            default:
                return StatusInProgressIcon;
        }
    });

    const statusText = computed(() => {
        const status = vardplatsbestallning?.value?.status;
        return status ?? "Under arbete";
    });

    const hasUnsavedData = computed(() => {
        return formRef?.value?.hasUnsavedData();
    });

    defineExpose({
        tabId,
        hasUnsavedData,
    });

    const formRef = ref();

    const submit = async (formData: any) => {
        try {
            if (vardplatsbestallning?.value) {
                formData.bioperationskoder = formData.bioperationskoder.map((opkod: { id: number }) => {
                    return opkod.id;
                });

                await store.dispatch("vardplatsbestallning/updateVardplatsbestallning", {
                    vardplatsbestallning: {
                        ...formData,
                        id: parameters?.value?.id,
                    },
                    config: {
                        tabbnamn: tabName,
                    },
                });

                flashMessage("Vårdplatsbeställningen uppdaterad");
            } else {
                formData.patient = store.state.patientData.currentId;
                formData.bioperationskoder = formData.bioperationskoder.map((opkod: { id: number }) => {
                    return opkod.id;
                });

                await store.dispatch("vardplatsbestallning/createVardplatsbestallning", {
                    vardplatsbestallning: formData,
                    config: {
                        tabbnamn: tabName,
                    },
                });

                flashMessage("Vårdplatsbeställningen sparad");
            }

            await store.dispatch("tabs/closeTab", tabId.value);
        } catch (e) {
            openDialog(getErrorMessage(e), "error");
        }
    };

    onBeforeMount(async () => {
        store.commit("vardplatsbestallning/addSubscriber");

        try {
            await store.dispatch("tabs/displaySpinner", {
                id: tabId?.value,
                display: true,
            });

            await store.dispatch("vardplatsbestallning/getVardplatsbestallningById", {
                id: parameters?.value?.id,
                config: {
                    tabbnamn: tabName,
                },
            });
        } catch (_) {
            flashWarning("Kunde inte hämta vårdplatsbeställning");
        } finally {
            await store.dispatch("tabs/displaySpinner", {
                id: tabId?.value,
                display: false,
            });
        }
    });

    onBeforeUnmount(() => {
        store.commit("vardplatsbestallning/removeSubscriber");
    });
</script>

<template>
    <div class="vardplatsbestallning">
        <div class="header-section">
            <h2>Vårdplatsbeställning</h2>
            <div class="metadata">
                <span v-if="vardplatsbestallning?.externt_id">
                    Operationsanmälan skapad {{ createdDate }} {{ createdBy }}
                </span>
                <span v-else>Vårdplatsbeställning skapad {{ createdDate }} {{ createdBy }}</span>
                <span><img :src="statusIcon" /> {{ statusText }}</span>
            </div>
        </div>
        <template v-if="!parameters?.id || vardplatsbestallning">
            <VardplatsbestallningForm
                v-if="!readOnly"
                ref="formRef"
                :vardplatsbestallningId="parameters?.id"
                @submit="submit"
                @cancel="cancel"
            />
            <VardplatsbestallningDisplay v-else :vardplatsbestallningId="parameters?.id" @cancel="cancel" />
        </template>
    </div>
</template>

<style lang="scss">
    @import "@/style/variables";
    @import "@/style/container";

    .vardplatsbestallning {
        .header-section {
            margin-bottom: 2rem;
            max-width: $content-max-width;

            .metadata {
                display: flex;
                justify-content: space-between;
                background: $color-background-tool-menu;
                padding: 16px 18px;
                border-radius: 6px;
                color: $color-text-popup;

                img {
                    margin-right: 2px;
                }
            }
        }
    }
</style>
